import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import VisitCard from "./VisitCard";
import VisitDetails from "./VisitDetails";
import { useEcrfContext } from "../../../../context/EcrfContext";
import useGetPatientVisitOverview from "../../../../hooks/useGetPatientVisitOverview";
import useUserActionsLogsMutation from "../../../../hooks/useUserActionsLogsMutation";
import { VISIT_STATUSES } from "../../../../utils/constants";

// Same order as the v1 visit tabs: screening, the scheduled visits, then the event visits
const orderVisits = (visits) => [
  ...visits.filter((visit) => visit.visit_number === -3),
  ...visits
    .filter((visit) => visit.visit_number >= 0)
    .sort((a, b) => a.visit_number - b.visit_number),
  ...visits.filter((visit) => visit.visit_number < 0 && visit.visit_number !== -3),
];

const EcrfVisitsV2 = ({
  setMessage,
  setTypeOfMessage,
  modalVisitChangeShow,
  setModalVisitChangeShow,
  setScheduleBaselineModalShow,
  singlePhotoSession,
}) => {
  const role = JSON.parse(localStorage.getItem("user_data"))?.role;
  const { allVisits, setSelectedVisit, trial, patientId, hasChanged, setSelectedSessionId } =
    useEcrfContext();
  const logAction = useUserActionsLogsMutation();

  const [expandedVisitId, setExpandedVisitId] = useState(null);
  const [hasUnsavedEcrfChanges, setHasUnsavedEcrfChanges] = useState(false);
  const [showEcrfUnsavedModal, setShowEcrfUnsavedModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const { data: overview = [] } = useGetPatientVisitOverview({ patientId, trialId: trial?.id });
  const overviewByVisitId = useMemo(
    () => Object.fromEntries(overview.map((entry) => [entry.visit_id, entry])),
    [overview]
  );

  const visits = useMemo(() => orderVisits(allVisits ?? []), [allVisits]);
  const completedCount = overview.filter(
    (entry) => entry.overall_visit_status === VISIT_STATUSES.COMPLETED
  ).length;

  useEffect(() => {
    setExpandedVisitId(null);
  }, [trial?.id]);

  // Opening another visit or form unmounts the current one, so unsaved answers are confirmed first
  const guard = (action) => {
    if (hasUnsavedEcrfChanges) {
      setPendingAction(() => action);
      setShowEcrfUnsavedModal(true);
    } else if (hasChanged) {
      setPendingAction(() => action);
      setModalVisitChangeShow(true);
    } else {
      action();
    }
  };

  // FullForm's "Unsaved Changes" dialog clears hasChanged on discard; run or drop the pending action once it closes
  useEffect(() => {
    if (modalVisitChangeShow || showEcrfUnsavedModal || !pendingAction) return;
    if (!hasChanged) pendingAction();
    setPendingAction(null);
  }, [modalVisitChangeShow, showEcrfUnsavedModal, hasChanged, pendingAction]);

  const toggleVisit = (visit) => {
    if (expandedVisitId === visit.id) {
      guard(() => setExpandedVisitId(null));
      return;
    }

    guard(() => {
      setSelectedSessionId(0);
      setSelectedVisit({ ...visit, start_date: new Date(visit.start_date + "Z") });
      setExpandedVisitId(visit.id);
      logAction.mutate({
        action_type: `Switched the visit to ${visit.visit_name}`,
        trial_name: trial?.name,
        patient_id: patientId,
      });
    });
  };

  const discardEcrfChanges = () => {
    setHasUnsavedEcrfChanges(false);
    setShowEcrfUnsavedModal(false);
    pendingAction?.();
    setPendingAction(null);
  };

  const keepEditing = () => {
    setShowEcrfUnsavedModal(false);
    setPendingAction(null);
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={0.5}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Visits
          </Typography>
          <Typography variant="body2" color="text.secondary">
            In-clinic eCRFs and app activities, visit by visit
          </Typography>
        </Box>
        {overview.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {completedCount} of {overview.length} scheduled visits completed
          </Typography>
        )}
      </Stack>

      <Stack spacing={1.5}>
        {!allVisits?.length
          ? [0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: 3 }} />
            ))
          : visits.map((visit) => (
              <VisitCard
                key={visit.id}
                visit={visit}
                overview={overviewByVisitId[visit.id]}
                expanded={expandedVisitId === visit.id}
                onToggle={() => toggleVisit(visit)}
              >
                <VisitDetails
                  role={role}
                  guard={guard}
                  setHasUnsavedEcrfChanges={setHasUnsavedEcrfChanges}
                  setMessage={setMessage}
                  setTypeOfMessage={setTypeOfMessage}
                  modalVisitChangeShow={modalVisitChangeShow}
                  setModalVisitChangeShow={setModalVisitChangeShow}
                  setScheduleBaselineModalShow={setScheduleBaselineModalShow}
                  singlePhotoSession={singlePhotoSession}
                />
              </VisitCard>
            ))}
      </Stack>

      <Dialog open={showEcrfUnsavedModal} onClose={keepEditing} maxWidth="sm" fullWidth>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography align="center" sx={{ mt: 1 }}>
            You have unsaved changes in the open eCRF. All of your unsaved changes will be lost if
            you continue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={discardEcrfChanges}>
            Discard Changes
          </Button>
          <Button variant="contained" onClick={keepEditing}>
            Continue Making Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EcrfVisitsV2;
